/**
 * Rótulo exibido para uma integração WhatsApp (Evolution), alinhado à página de Integrações:
 * friendly_name → primeiro segmento de instance_identifier → instance_name → fallback.
 */
import { formatInternationalPhoneForDisplay } from "./format-phone.ts";

export type WhatsAppIntegrationLike = {
  instance_identifier?: string | null;
  provider?: string | null;
  config?: {
    friendly_name?: string | null;
    instance_name?: string | null;
    phone_number?: string | null;
    /** WhatsApp Cloud (Embedded Signup): número formatado E.164 */
    display_phone_number?: string | null;
    /** WhatsApp Cloud: nome verificado pela Meta (verified_name) */
    verified_name?: string | null;
  } | null;
} | null | undefined;

/** Normaliza rótulo só na UI (ex.: underscores do sanitize do create). */
export function humanizeDisplayLabel(label: string): string {
  return label.replace(/_/g, " ").replace(/\s+/g, " ").trim();
}

/** Nome para exibição: Fabchat, Minha Loja, etc. */
export function formatDisplayName(label: string): string {
  const humanized = humanizeDisplayLabel(label);
  if (!humanized) return "";

  return humanized
    .split(" ")
    .map((word) => {
      if (!word) return "";
      if (word.length <= 4 && word === word.toUpperCase()) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function whatsappIntegrationDisplayLabel(
  integration: WhatsAppIntegrationLike,
  fallback = "WhatsApp"
): string {
  const cfg = integration?.config;

  // WhatsApp Business (Baileys novo) — mesma heurística que Evolution
  if (integration?.provider === "whatsapp_uazapi") {
    const friendly = typeof cfg?.friendly_name === "string" ? cfg.friendly_name.trim() : "";
    if (friendly) return humanizeDisplayLabel(friendly);

    const id = integration?.instance_identifier;
    if (id) {
      const first = id.split("_")[0];
      if (first) return humanizeDisplayLabel(first);
    }

    const instName = typeof cfg?.instance_name === "string" ? cfg.instance_name.trim() : "";
    if (instName) {
      if (looksLikeEmail(instName)) return instName;
      return humanizeDisplayLabel(instName);
    }

    return fallback === "WhatsApp" ? "WhatsApp Business" : fallback;
  }

  // WhatsApp Cloud: priorizar dados oficiais Meta (display_phone_number / verified_name)
  if (integration?.provider === "whatsapp_cloud") {
    const friendly = typeof cfg?.friendly_name === "string" ? cfg.friendly_name.trim() : "";
    if (friendly) return formatDisplayName(friendly);
    const display = typeof cfg?.display_phone_number === "string" ? cfg.display_phone_number.trim() : "";
    if (display) return formatInternationalPhoneForDisplay(display);
    const verified = typeof cfg?.verified_name === "string" ? cfg.verified_name.trim() : "";
    if (verified) return formatDisplayName(verified);
    return fallback;
  }

  const friendly = typeof cfg?.friendly_name === "string" ? cfg.friendly_name.trim() : "";
  if (friendly) return humanizeDisplayLabel(friendly);

  const id = integration?.instance_identifier;
  if (id) {
    const first = id.split("_")[0];
    if (first) return humanizeDisplayLabel(first);
  }

  const instName = typeof cfg?.instance_name === "string" ? cfg.instance_name.trim() : "";
  if (instName) {
    if (looksLikeEmail(instName)) return instName;
    return humanizeDisplayLabel(instName);
  }

  return fallback;
}

/**
 * Partes para seletores (templates, campanhas): nome em destaque + telefone formatado.
 */
export function whatsappCloudIntegrationSelectParts(
  integration: WhatsAppIntegrationLike,
  fallback = "Número não disponível",
): { primary: string; secondary: string | null } {
  const cfg = integration?.config;
  const phoneRaw = typeof cfg?.display_phone_number === "string" ? cfg.display_phone_number.trim() : "";
  const phone = phoneRaw ? formatInternationalPhoneForDisplay(phoneRaw) : "";
  const verified = typeof cfg?.verified_name === "string" ? cfg.verified_name.trim() : "";
  const friendly = typeof cfg?.friendly_name === "string" ? cfg.friendly_name.trim() : "";

  const name = formatDisplayName(verified || friendly);
  const phoneDigits = phoneRaw.replace(/\D/g, "");
  const nameLooksLikePhone = name && phoneDigits && name.replace(/\D/g, "") === phoneDigits;

  if (name && phone && !nameLooksLikePhone) {
    return { primary: name, secondary: phone };
  }
  if (phone) return { primary: phone, secondary: name || null };
  if (name) return { primary: name, secondary: null };
  return { primary: fallback, secondary: null };
}

/**
 * Rótulo para seletores (templates, campanhas): prioriza o número exibido pela Meta.
 * Útil quando há várias integrações Cloud no mesmo workspace.
 */
export function whatsappCloudIntegrationSelectLabel(
  integration: WhatsAppIntegrationLike,
  fallback = "Número não disponível",
): string {
  const { primary, secondary } = whatsappCloudIntegrationSelectParts(integration, fallback);
  if (secondary) return `${primary} · ${secondary}`;
  return primary;
}

/** Título e subtítulo da sidebar para WhatsApp Cloud (nome verificado em cima, telefone embaixo). */
export function whatsappCloudSidebarLabels(
  integration: WhatsAppIntegrationLike,
  fallback = "WhatsApp"
): { title: string; subtitle: string } {
  const cfg = integration?.config;
  const friendly = typeof cfg?.friendly_name === "string" ? cfg.friendly_name.trim() : "";
  const verified = typeof cfg?.verified_name === "string" ? cfg.verified_name.trim() : "";
  const phoneRaw = typeof cfg?.display_phone_number === "string" ? cfg.display_phone_number.trim() : "";
  const phone = phoneRaw ? formatInternationalPhoneForDisplay(phoneRaw) : "";

  const title =
    formatDisplayName(verified) ||
    (friendly ? formatDisplayName(friendly) : "") ||
    fallback;
  return { title, subtitle: phone };
}
